import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useCompany } from '../../hooks/useCompany'
import LandingPage from '../LandingPage'

export function LandingPageGuard() {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { user, companyId, isAdmin, passwordRecoveryPending, loading: authLoading } = useAuth()
  const { settings, loading: settingsLoading } = useCompany()

  // Wait for session + membership resolve so SSO users are not stuck on marketing
  // before companyId is known (and before a false earlySignalsAccessDenied sticks).
  if (authLoading || settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <i className="fa-solid fa-spinner fa-spin text-3xl text-gray-600" aria-hidden />
        <span className="sr-only">Loading...</span>
      </div>
    )
  }

  // When user explicitly asks to view the landing page (e.g. clicked Home or logo from Early Signals), show it even when logged in
  const explicitViewLanding = searchParams.get('view') === 'landing'
  const deniedFromEsGuard = location.state?.earlySignalsAccessDenied === true

  // Align with EarlySignalsGuard: undefined = enabled, only explicit false disables
  const earlySignalsEnabled = settings?.features?.earlySignals !== false
  const hasEarlySignalsAccess = !!(user && (isAdmin || (companyId && earlySignalsEnabled)))

  // Forgot-password recovery must stay on LandingPage (AuthModal) — do not bounce to Early Signals
  if (passwordRecoveryPending) {
    return <LandingPage />
  }

  // Recover from a premature deny redirect once membership has loaded
  if (hasEarlySignalsAccess && deniedFromEsGuard) {
    return <Navigate to="/early-signals" replace />
  }

  // If authenticated user has Early Signals access and did not ask to see landing, redirect to dashboard
  if (hasEarlySignalsAccess && !explicitViewLanding) {
    return <Navigate to="/early-signals" replace />
  }

  // Show landing page: unauthenticated, no access, or explicit view=landing
  return <LandingPage />
}
